// Tracks whether the user's last interaction was keyboard or pointer and
// exposes it as <html data-focus-modality="keyboard|pointer">, so CSS can
// show focus rings for keyboard focus only.
//
// Plain :focus-visible isn't enough for our search fields: browsers always
// treat a focused text input as focus-visible, even after a mouse click.
// Mirrors React Aria's useFocusVisible rules (which the NF UI library uses
// internally): starts as "keyboard", any meaningful key press switches to
// "keyboard", any pointer press switches to "pointer".

type FocusModality = "keyboard" | "pointer";

const isMac = typeof navigator !== "undefined" && /^Mac/i.test(navigator.platform);

// Same filter as React Aria — modifier-only presses and shortcuts
// (Cmd/Ctrl+key) don't count as keyboard navigation.
const isValidKey = (e: KeyboardEvent) =>
  !(
    e.metaKey ||
    (!isMac && e.altKey) ||
    e.ctrlKey ||
    e.key === "Control" ||
    e.key === "Shift" ||
    e.key === "Meta"
  );

const setModality = (modality: FocusModality) => {
  if (document.documentElement.dataset.focusModality !== modality) {
    document.documentElement.dataset.focusModality = modality;
  }
};

let installed = false;

export const installFocusModality = () => {
  if (installed || typeof document === "undefined") return;
  installed = true;

  setModality("keyboard");

  // Capture phase so the modality is set before any focus handler runs.
  document.addEventListener("keydown", (e) => { if (isValidKey(e)) setModality("keyboard"); }, true);
  document.addEventListener("pointerdown", () => setModality("pointer"), true);
  document.addEventListener("mousedown", () => setModality("pointer"), true);
};
